import { useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { startLoadding, stopLoading } from '../../../../Redux/FrontEnd/Loading/LoadingSlice'
import { ApiWalletCardsResponse } from '../types'
import { useWalletCardApi } from '../services/walletCardTemplateApi'
import { defaultPage, defaultPageSize } from '../constants'

export function useFetchWalletCardTemplates() {
  const dispatch = useDispatch()
  const { getWalletCardList, deleteWalletCardTemplate, updateWalletCardTemplate } = useWalletCardApi()
  const [error, setError] = useState<string | null>(null)
  const [hasApiError, setHasApiError] = useState(false)
  const [templates, setTemplates] = useState<ApiWalletCardsResponse>({ items: [], total: 0, totalPages: 0, current: 1 })
  const [page, setPage] = useState({ page: defaultPage, size: defaultPageSize })
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const fetchData = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (isLoading || hasApiError) return

    dispatch(startLoadding())
    setError(null)
    setIsLoading(true)
    try {
      const response: any = await getWalletCardList({ page: page.page, limit: page.size })
      if (response.data.data) {
        setTemplates(response.data.data)
        setHasApiError(false)
      } else if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch data')
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch data')
      toast.error(err?.message || 'Failed to fetch data')
      setHasApiError(true)
    } finally {
      dispatch(stopLoading())
      setIsLoading(false)
    }
  }, [page, getWalletCardList, hasApiError, dispatch])

  const retryFetchData = () => {
    setHasApiError(false)
    fetchData()
  }

  const handleDelete = async (id: string, onSuccess?: () => void) => {
    setIsDeleting(true)
    try {
      dispatch(startLoadding())
      const res: any = await deleteWalletCardTemplate({ id })
      if (res.data.status) {
        toast.success(res.data.message)
        if (templates.items.length === 1 && page.page > 1) {
          setPage(prev => ({ ...prev, page: prev.page - 1 }))
        } else {
          fetchData()
        }
        if (onSuccess) onSuccess()
      } else {
        toast.error(res.data.message)
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete template')
    } finally {
      dispatch(stopLoading())
      setIsDeleting(false)
    }
  }

  const handleSetDefault = async (templateData: any, onSuccess?: () => void) => {
    try {
      dispatch(startLoadding())
      const res: any = await updateWalletCardTemplate({
        ...templateData,
        isDefault: true
      })
      if (res?.data?.status) {
        toast.success('Template set as default successfully!')
        fetchData()
        if (onSuccess) onSuccess()
      } else {
        toast.error(res?.data?.message || 'Failed to set template as default')
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to set template as default')
    } finally {
      dispatch(stopLoading())
    }
  }

  const handlePageChange = useCallback((selectedPage: number) => {
    setHasApiError(false)
    setPage(prev => ({ ...prev, page: selectedPage + 1 }))
  }, [])

  return {
    templates,
    setTemplates,
    page,
    setPage,
    error,
    isLoading,
    isDeleting,
    hasApiError,
    fetchData,
    retryFetchData,
    handleDelete,
    handleSetDefault,
    handlePageChange,
  }
}
